// src/data/blogData.js
//
// Articles publiés par ATAA REALTY. Chaque article est composé d'un en-tête
// (titre, sous-titre, date, catégorie, image, excerpt) et d'un tableau de
// blocs `content` rendus par src/pages/BlogDetail.js.
//
// Types de blocs supportés :
//   { type: 'h2',         text }              section
//   { type: 'h3',         text }              sous-section
//   { type: 'p',          text }              paragraphe (inline **gras** / *italique*)
//   { type: 'list',       items: [string] }   puces
//   { type: 'formula',    text }              encart formule centré
//   { type: 'callout',    title, items }      encart à puces (ex. « L'essentiel en N points »)
//   { type: 'table',      headers, rows }     tableau
//   { type: 'faq',        items: [{q,a}] }    foire aux questions
//   { type: 'disclaimer', text }              mention pédagogique
//   { type: 'references', items: [string] }   références bibliographiques

export const blogData = [
  /* =====================================================================
   *  1. Taxe sur le Profit Immobilier (TPI)
   * ===================================================================== */
  {
    id: 1,
    date: '20 Janvier 2026',
    category: 'Fiscalité',
    title:
      "Taxe sur le Profit Immobilier (TPI) au Maroc : tout comprendre sur la nouvelle procédure d’avis préalable de l’administration fiscale",
    subtitle:
      'Vendre, déclarer, sécuriser : ce que les lois de finances récentes ont changé pour les vendeurs de biens immobiliers',
    image:
      'https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2000',
    excerpt:
      "Les lois de finances récentes ont introduit une option d’avis préalable de l’administration sur la TPI. Comparatif méthodique avec la procédure classique, cas chiffré et apport de l’expertise immobilière.",
    content: [
      { type: 'h2', text: 'Introduction : pourquoi cet article ?' },
      {
        type: 'p',
        text:
          "Au Maroc, la vente d’un appartement, d’une maison, d’un terrain ou de tout droit réel immobilier déclenche, sauf exonération, un impôt bien connu des vendeurs : la **Taxe sur le Profit Immobilier (TPI)**. Pendant longtemps, la règle du jeu était simple, mais inconfortable : le vendeur calculait lui-même son impôt, le déclarait et le payait dans les trente jours, puis vivait pendant quatre années sous la menace d’un redressement, sans jamais savoir si l’administration validerait son calcul.",
      },
      {
        type: 'p',
        text:
          "Les dernières lois de finances ont introduit une option supplémentaire : la possibilité de demander, **avant même de vendre**, l’avis de l’administration fiscale sur le montant exact de la taxe due. Cet article, conçu dans une logique pédagogique, explique pas à pas cette nouvelle procédure d’avis préalable, la met en regard de la procédure classique toujours en vigueur, et l’illustre par un cas chiffré. L’objectif n’est pas de désigner une « meilleure » voie, mais de présenter objectivement les deux afin que chaque vendeur — comme chaque conseil — choisisse celle qui correspond le mieux à sa situation.",
      },

      { type: 'h2', text: '1. Définition : qu’est-ce que la TPI ?' },
      {
        type: 'p',
        text:
          "La TPI est, juridiquement, une composante de l’**impôt sur le revenu au titre des profits fonciers (IR/PF)**. Elle frappe le profit net — c’est-à-dire la plus-value — réalisé à l’occasion de la cession à titre onéreux d’immeubles situés au Maroc ou de droits réels immobiliers s’y rattachant. Sont notamment concernées les ventes, les échanges (considérés comme une double vente), certains apports en société, ou encore l’expropriation pour cause d’utilité publique.",
      },
      {
        type: 'p',
        text:
          "Le taux est aujourd’hui **unifié à 20 %** du profit net imposable depuis la loi de finances 2023 (auparavant, la première vente d’un terrain non bâti en périmètre urbain était taxée à 30 %). La loi prévoit par ailleurs un **minimum de perception de 3 %** du prix de cession : si l’impôt calculé sur la plus-value est inférieur à ce seuil, c’est ce minimum qui s’applique.",
      },
      { type: 'p', text: '**Principales exonérations à connaître (article 63 du CGI) :**' },
      {
        type: 'list',
        items: [
          "la cession d’une **habitation principale** occupée depuis au moins 5 ans, dans la limite d’un prix de 4 000 000 DH ;",
          'les cessions dont la valeur totale annuelle n’excède pas 140 000 DH ;',
          "la cession d’un **logement social** affecté à l’habitation principale depuis au moins 4 ans ;",
          'les cessions à titre gratuit entre ascendants et descendants, entre époux, ou entre frères et sœurs.',
        ],
      },

      { type: 'h2', text: '2. Le cadre juridique et réglementaire marocain' },
      {
        type: 'p',
        text:
          "La TPI est régie par le **Code Général des Impôts (CGI)**, actualisé chaque année par la loi de finances. Plusieurs articles s’articulent pour encadrer le calcul, la déclaration, le paiement et le contrôle de l’impôt :",
      },
      {
        type: 'table',
        headers: ['Article du CGI', 'Objet'],
        rows: [
          ['61 à 65', "Champ d’application, assiette, taux et coefficients de réévaluation de la TPI."],
          ['83', 'Obligation de déclaration des profits fonciers dans les 30 jours de la cession.'],
          ['173', 'Recouvrement par paiement spontané et règle du versement provisoire de 5 %.'],
          ['224', 'Procédure de rectification en matière de profits fonciers.'],
          ['232', "Délai de prescription : droit de reprise jusqu’au 31 décembre de la 4ᵉ année."],
          ['234 quinquies', "Demande d’avis préalable de l’administration (introduit par la LF 2023)."],
        ],
      },
      {
        type: 'p',
        text:
          "La nouveauté centrale est l’**article 234 quinquies**, créé par la **loi de finances n° 50-22 pour 2023** et complété par les lois de finances suivantes (notamment la **loi n° 60-24 pour 2025**, qui a retouché les articles 83, 173 et 224). C’est ce texte qui fonde la procédure d’avis préalable.",
      },

      { type: 'h2', text: '3. Analyse méthodologique : comment se calcule la TPI ?' },
      {
        type: 'p',
        text:
          'Avant de comparer les deux procédures, il faut comprendre la mécanique du calcul. Le **profit net imposable** est égal à la différence entre :',
      },
      {
        type: 'list',
        items: [
          'le **prix de cession** (prix de vente), diminué le cas échéant des frais de cession justifiés (courtage, annonces, frais d’actes, indemnités d’éviction) ;',
          'et le **prix d’acquisition réévalué**, augmenté des frais d’acquisition, des dépenses d’investissement et, le cas échéant, des intérêts d’emprunt.',
        ],
      },
      {
        type: 'p',
        text:
          "Le prix d’acquisition est majoré de **frais d’acquisition forfaitaires de 15 %** (frais de notaire, droits d’enregistrement, conservation foncière, courtage…), sauf si le vendeur justifie de frais réels supérieurs. S’y ajoutent les **dépenses d’investissement** dûment justifiées par factures (construction, rénovation, amélioration). L’ensemble est ensuite réévalué par un **coefficient fixé chaque année** par arrêté ministériel, fondé sur l’indice national du coût de la vie.",
      },
      {
        type: 'formula',
        text:
          "TPI = 20 % × (Prix de cession − Prix d’acquisition réévalué)  —  avec un minimum de 3 % du prix de cession",
      },

      { type: 'h3', text: 'Deux voies possibles pour le vendeur' },
      {
        type: 'p',
        text:
          "Une fois le principe du calcul posé, le vendeur dispose de **deux procédures** pour s’acquitter de la TPI. Elles sont l’une et l’autre parfaitement légales et répondent à des logiques différentes : l’une privilégie la **simplicité et la rapidité**, l’autre la **prévisibilité du montant**. Le choix dépend de la nature de l’opération, de son montant, du calendrier et du degré de certitude recherché.",
      },

      { type: 'h3', text: 'A. La voie classique : déclaration et paiement spontanés (articles 83 et 173)' },
      {
        type: 'p',
        text:
          "C’est la procédure historique, toujours pleinement valable et souvent la plus directe. Le vendeur réalise la transaction, puis dépose sa **déclaration des profits fonciers (article 83)** et paie spontanément l’impôt correspondant dans les **30 jours suivant la date de la cession (article 173)**. Elle a l’avantage de ne dépendre d’aucun délai d’instruction préalable et convient particulièrement aux opérations simples, de faible montant ou clairement exonérées. En contrepartie, l’administration conserve son droit de contrôle : elle peut rectifier la base déclarée (article 224) et engager un redressement jusqu’au **31 décembre de la 4ᵉ année** suivant celle de la cession (prescription, article 232).",
      },
      {
        type: 'p',
        text:
          "Depuis 2023, une règle particulière encadre cette voie : le vendeur qui n’a pas sollicité l’avis préalable — ou qui n’a pas déclaré sur la base de l’attestation de liquidation — doit verser, à titre provisoire, **la différence entre l’impôt qu’il déclare et 5 % du prix de cession**. Cette somme provisoire n’est pas définitivement perdue : elle est **rétrocédée au contribuable** lors de l’engagement de la procédure de rectification. Elle agit comme une garantie de trésorerie au profit du Trésor.",
      },

      { type: 'h3', text: "B. La voie de l’avis préalable : liquidation par l’administration (article 234 quinquies)" },
      {
        type: 'p',
        text:
          "C’est l’option introduite récemment. Le vendeur peut demander à l’administration de **liquider l’impôt à l’avance**. Elle suppose en revanche d’anticiper et de constituer un dossier complet dès le compromis, et d’intégrer le délai d’instruction au calendrier de la vente. Concrètement, la demande :",
      },
      {
        type: 'list',
        items: [
          'est déposée par voie **électronique**, selon un modèle de l’administration, **dans les 30 jours** suivant la date du compromis de vente ;',
          'est accompagnée des pièces justificatives relatives au calcul (prix, frais, dépenses d’investissement) ou à l’exonération sollicitée ;',
          "reçoit une **réponse de l’administration dans un délai de 60 jours**. Cette réponse vaut **attestation de liquidation** de l’impôt (ou d’exonération) et reste valable **6 mois**.",
        ],
      },
      {
        type: 'p',
        text:
          "La conséquence est la suivante : si le vendeur déclare et paie sur la base de cette attestation, il est **dispensé de contrôle fiscal** en matière de profits fonciers, le montant ayant été arrêté avec l’administration. La démarche demeure facultative : le vendeur reste libre d’opter pour la voie classique, et l’avis préalable n’est pas toujours compatible avec un calendrier de vente très serré.",
      },

      { type: 'h3', text: 'Tableau comparatif des deux procédures' },
      {
        type: 'table',
        headers: ['Critère', 'Voie classique', 'Avis préalable'],
        rows: [
          ['Moment', 'Après la vente', 'Après le compromis, avant la vente'],
          ['Délai à respecter', 'Déclaration + paiement sous 30 j de la cession', 'Demande sous 30 j du compromis ; réponse sous 60 j'],
          ['Qui calcule l’impôt ?', 'Le vendeur (sous sa responsabilité)', 'L’administration (attestation de liquidation)'],
          ['Risque de redressement', 'Maintenu jusqu’à la 4ᵉ année', 'Écarté si déclaration conforme à l’attestation'],
          ['Versement provisoire de 5 %', 'Applicable (puis rétrocédé)', 'Non applicable'],
          ['Atout principal', 'Simplicité et rapidité d’exécution', 'Prévisibilité du montant et sécurité juridique'],
          ['Point d’attention', 'Contrôle possible et avance de trésorerie', 'Anticipation et dossier complet dès le compromis'],
          ['Caractère', 'Procédure de droit commun', 'Option au choix du vendeur'],
        ],
      },

      { type: 'h2', text: '4. Cas pratique : un appartement vendu à Casablanca' },
      {
        type: 'p',
        text:
          "Prenons l’exemple de **Mme A.**, qui a acquis un appartement en 2015 pour 1 000 000 DH et le revend en 2026 pour 1 800 000 DH. Elle a réalisé 100 000 DH de travaux justifiés par factures. Le coefficient de réévaluation retenu est de 1,10 (valeur illustrative).",
      },
      {
        type: 'table',
        headers: ['Étape de calcul', 'Montant (DH)'],
        rows: [
          ['Prix d’acquisition (2015)', '1 000 000'],
          ['+ Frais d’acquisition forfaitaires (15 %)', '150 000'],
          ['+ Dépenses d’investissement justifiées', '100 000'],
          ['= Prix de revient', '1 250 000'],
          ['× Coefficient de réévaluation (1,10)', '1 375 000'],
          ['Prix de cession (2026)', '1 800 000'],
          ['= Profit net imposable', '425 000'],
          ['TPI au taux de 20 %', '85 000'],
          ['Minimum de perception (3 % de 1 800 000)', '54 000'],
          ['**Impôt finalement dû** (le plus élevé)', '**85 000**'],
        ],
      },
      {
        type: 'p',
        text:
          "**Lecture selon la voie choisie.** Dans les deux cas, l’impôt dû s’élève à 85 000 DH. Si Mme A. opte pour l’**avis préalable**, elle dépose sa demande dans les 30 jours du compromis et reçoit une attestation fixant ce montant ; en déclarant et payant sur cette base, sa situation n’est plus susceptible de contrôle. Si elle suit la **voie classique**, elle déclare et paie spontanément 85 000 DH dans les 30 jours de la cession ; n’ayant pas sollicité d’avis, elle verse en outre, à titre provisoire, la différence avec 5 % du prix de cession (90 000 − 85 000 = 5 000 DH), somme restituée lors d’une éventuelle procédure de rectification. La première voie fige le montant en amont, la seconde est plus rapide à mettre en œuvre mais laisse ouvert le droit de contrôle pendant quatre ans.",
      },

      { type: 'h2', text: '5. À retenir' },
      {
        type: 'callout',
        title: 'L’essentiel en 5 points',
        items: [
          '**Taux unique :** la TPI est de 20 % du profit net, avec un minimum de 3 % du prix de cession.',
          '**Deux voies au choix, également légales :** la déclaration spontanée classique (articles 83/173) et l’avis préalable (article 234 quinquies).',
          '**Voie classique :** simple et rapide ; l’administration conserve son droit de rectification jusqu’à la 4ᵉ année et un versement provisoire de 5 % du prix s’applique.',
          '**Avis préalable :** demande dans les 30 jours du compromis, réponse sous 60 jours, attestation valable 6 mois ; suppose un dossier complet anticipé.',
          '**Critère de choix :** montant et complexité de l’opération, calendrier de la vente et niveau de certitude recherché.',
        ],
      },

      { type: 'h2', text: '6. Foire aux questions (FAQ)' },
      {
        type: 'faq',
        items: [
          {
            q: 'L’avis préalable est-il obligatoire ?',
            a: 'Non. C’est une **faculté** offerte au vendeur. Celui qui ne l’utilise pas reste soumis à la procédure classique de déclaration et de paiement spontanés.',
          },
          {
            q: 'À partir de quand court le délai de 30 jours ?',
            a: 'Pour l’avis préalable, le délai de 30 jours se compte à partir de la **date du compromis de vente**. Pour la déclaration classique, il se compte à partir de la **date de la cession** (l’acte définitif).',
          },
          {
            q: 'Combien de temps l’attestation de liquidation reste-t-elle valable ?',
            a: '**Six mois** à compter de sa délivrance. La vente doit donc être finalisée dans ce délai pour bénéficier de la sécurité associée.',
          },
          {
            q: 'Que se passe-t-il si je ne demande pas l’avis préalable ?',
            a: 'Vous déclarez et payez vous-même, mais devez verser à titre provisoire la différence entre l’impôt déclaré et **5 % du prix de cession**. Cette somme est restituée lors de l’engagement de la procédure de rectification, le cas échéant.',
          },
          {
            q: 'Puis-je encore être redressé après avoir suivi l’avis préalable ?',
            a: '**Non**, dès lors que votre déclaration et votre paiement sont strictement conformes aux éléments de l’attestation de liquidation. À défaut de conformité, la rectification de l’article 224 redevient possible.',
          },
          {
            q: 'Pendant combien de temps l’administration peut-elle contrôler une vente classique ?',
            a: 'Jusqu’au **31 décembre de la quatrième année** suivant celle où la cession lui a été révélée (par l’enregistrement de l’acte ou le dépôt de la déclaration).',
          },
        ],
      },

      { type: 'h2', text: '7. L’apport d’un rapport d’expertise ATAA REALTY dans les deux procédures' },
      {
        type: 'p',
        text:
          "Quelle que soit la voie retenue, la TPI repose sur des **valeurs** : un prix de cession, un prix d’acquisition, des frais et des dépenses d’investissement. C’est précisément sur l’appréciation de ces valeurs que se concentrent les difficultés et les éventuels désaccords avec l’administration. Un **rapport d’expertise immobilière indépendant**, établi par un expert MRICS, apporte une base documentée et argumentée qui sert l’une comme l’autre des deux procédures.",
      },
      { type: 'h3', text: 'Dans la procédure d’avis préalable' },
      {
        type: 'list',
        items: [
          'il solidifie le dossier remis à l’administration en justifiant la **valeur vénale** et la cohérence du prix annoncé ;',
          'il documente les **dépenses d’investissement** et les frais à intégrer au prix de revient, ce qui fiabilise l’attestation de liquidation ;',
          'il réduit les allers-retours et les demandes de compléments, et donc le risque de dépasser les délais.',
        ],
      },
      { type: 'h3', text: 'Dans la procédure classique' },
      {
        type: 'list',
        items: [
          'il appuie le profit déclaré par une **estimation motivée** de la valeur du bien, utile si l’administration procède à une réestimation (article 224) ;',
          'il constitue une **pièce de défense** en cas de contrôle ou de redressement, en démontrant le sérieux et la traçabilité des montants retenus ;',
          'il aide à **anticiper le montant** de la taxe et le versement provisoire de 5 %, pour mieux préparer sa trésorerie.',
        ],
      },
      {
        type: 'p',
        text:
          "En amont de toute vente, l’expertise permet aussi de **vérifier l’éligibilité à une exonération** (habitation principale, seuils, durée de détention), d’**estimer la TPI dès la négociation** et d’**éclairer le choix** entre les deux voies. Chez ATAA REALTY, nos rapports d’expertise sont conçus pour offrir cette sécurité documentaire, au service du vendeur comme de son conseil, sans préjuger de la procédure finalement retenue.",
      },

      { type: 'h2', text: 'Conclusion' },
      {
        type: 'p',
        text:
          "La fiscalité immobilière marocaine offre désormais au vendeur **deux procédures complémentaires** pour s’acquitter de la TPI. La voie classique privilégie la simplicité et la rapidité ; l’avis préalable privilégie la prévisibilité du montant. Aucune n’est, en soi, supérieure à l’autre : le bon choix dépend de la complexité de l’opération, de son montant, du calendrier de la vente et du niveau de certitude souhaité.",
      },
      {
        type: 'p',
        text:
          "L’essentiel est de choisir en connaissance de cause et de s’appuyer sur des **valeurs justes et documentées**. Chez **ATAA REALTY**, nous accompagnons vendeurs et acquéreurs dans l’évaluation du bien, le calcul de la TPI et l’analyse de la procédure la mieux adaptée à chaque situation.",
      },
      {
        type: 'disclaimer',
        text:
          "Cet article est fourni à titre d’information pédagogique et ne constitue pas un conseil fiscal personnalisé. Pour une opération précise, rapprochez-vous d’un professionnel de l’immobilier ou de la fiscalité.",
      },

      { type: 'h2', text: 'Références bibliographiques et réglementaires' },
      {
        type: 'references',
        items: [
          "**Code Général des Impôts (CGI), édition 2026** — articles 61 à 65 (assiette et taux de la TPI), 83 (déclaration des profits fonciers), 173 (recouvrement et versement provisoire de 5 %), 224 (rectification), 232 (prescription) et 234 quinquies (demande d’avis préalable).",
          "**Loi de finances n° 50-22 pour l’année budgétaire 2023** — institution de l’article 234 quinquies et unification du taux de la TPI à 20 %.",
          "**Loi de finances n° 60-24 pour l’année budgétaire 2025** — modifications des articles 83, 173 et 224 du CGI.",
          "**Direction Générale des Impôts (DGI)**, Guide de demande de l’avis préalable de l’administration en matière d’IR/PF (www.tax.gov.ma).",
          "Support de formation « Fiscalité immobilière », **ONIGT–CRCS**, M. Salah Eddine EL KACIMI, MRICS, janvier 2026.",
        ],
      },
    ],
  },

  /* =====================================================================
   *  2. Taxe sur les Terrains Urbains Non Bâtis (TNB) — Réforme 2026
   * ===================================================================== */
  {
    id: 2,
    date: '27 Janvier 2026',
    category: 'Fiscalité locale',
    title:
      'La Taxe sur les Terrains Urbains Non Bâtis (TNB) au Maroc : fonctionnement et la réforme 2026',
    subtitle:
      "Comprendre ce que paie un propriétaire de terrain — et le nouveau barème fondé sur le niveau d’équipement introduit par la loi n° 14-25",
    image:
      'https://images.unsplash.com/photo-1486325212027-8081e485255e?q=80&w=2000',
    excerpt:
      "Avec la loi n° 14-25, le tarif de la TNB ne dépend plus de la catégorie urbanistique mais du niveau d’équipement de la zone. Décryptage de la réforme 2026, exonérations et cas pratique.",
    content: [
      { type: 'h2', text: 'Introduction : pourquoi cet article ?' },
      {
        type: 'p',
        text:
          "Détenir un terrain nu en ville n’est jamais totalement « neutre » fiscalement. Tant qu’il n’est pas construit, ce terrain est en principe redevable d’une **taxe locale annuelle** : la **Taxe sur les Terrains Urbains Non Bâtis (TNB)**. Souvent sous-estimée par les propriétaires, elle pèse pourtant chaque année sur le coût de portage d’un foncier et constitue un levier des communes pour lutter contre la rétention spéculative.",
      },
      {
        type: 'p',
        text:
          "La **loi n° 14-25** — qui modifie la loi n° 47-06 relative à la fiscalité des collectivités territoriales — a profondément réformé cette taxe. À compter de **2026**, son barème ne dépend plus de la catégorie urbanistique du terrain, mais du **niveau réel d’équipement** dont il bénéficie. Cet article explique d’abord le fonctionnement de la TNB, puis détaille cette réforme et ses conséquences concrètes pour les propriétaires fonciers.",
      },

      { type: 'h2', text: '1. Définition : qu’est-ce que la TNB ?' },
      {
        type: 'p',
        text:
          "La TNB est une **taxe locale annuelle**, gérée par les services fiscaux des communes, qui frappe les **terrains urbains non bâtis** — c’est-à-dire les parcelles situées en zone urbaine et qui ne supportent aucune construction (ou dont l’aménagement reste négligeable). Elle vise à inciter à la mise en valeur du foncier : un terrain laissé nu coûte chaque année à son propriétaire, ce qui décourage la spéculation et la thésaurisation de parcelles constructibles.",
      },
      {
        type: 'p',
        text:
          "Elle se distingue de la **taxe d’habitation** et de la **taxe de services communaux**, qui portent sur les immeubles bâtis. Dès qu’une construction est édifiée — et achevée — la parcelle bascule, en règle générale, hors du champ de la TNB.",
      },

      { type: 'h2', text: '2. Le cadre juridique' },
      {
        type: 'p',
        text:
          "La TNB est régie par la **loi n° 47-06** relative à la fiscalité des collectivités territoriales (articles 39 à 49), modifiée et complétée d’abord par la **loi n° 07-20**, puis, plus récemment, par la **loi n° 14-25** promulguée en juin 2025, qui porte la réforme applicable dès 2026.",
      },
      {
        type: 'table',
        headers: ['Article', 'Objet'],
        rows: [
          ['39 – 40', 'Biens et personnes imposables (propriétaire ou, à défaut, possesseur).'],
          ['41 – 42', 'Exonérations permanentes et temporaires (lotissement, permis de construire, etc.).'],
          ['43 – 44', 'Base imposable (superficie au m²) et annualité (faits au 1ᵉʳ janvier).'],
          ['45', '**Tarif** — entièrement réécrit par la loi n° 14-25 (réforme 2026).'],
          ['46 – 48', 'Paiement (avant le 1ᵉʳ mars) et obligations déclaratives.'],
        ],
      },

      { type: 'h2', text: '3. Comment fonctionne la TNB ?' },
      { type: 'h3', text: 'Qui paie, et sur quels terrains ?' },
      {
        type: 'p',
        text:
          "La taxe est due par le **propriétaire** du terrain et, à défaut de propriétaire connu, par le **possesseur**. En cas d’indivision, elle est établie sur l’indivision, chaque co-indivisaire restant solidairement tenu du paiement. Sont concernés les terrains non bâtis situés dans les **périmètres urbains**, les centres délimités, les stations (estivales, hivernales, thermales) et les zones couvertes par un **plan d’aménagement**.",
      },

      { type: 'h3', text: 'La règle des « 5 fois la superficie couverte »' },
      {
        type: 'p',
        text:
          "Un point essentiel concerne les terrains attenants à une construction. Lorsqu’une villa est édifiée sur une grande parcelle, la partie de terrain correspondant à **5 fois la superficie couverte** est exonérée de la TNB ; seul l’excédent, au-delà de ce seuil, est frappé par la TNB.",
      },
      {
        type: 'callout',
        title: 'Exemple — terrain de 8 000 m² avec une villa',
        items: [
          'Superficie couverte totale de la villa (sous-sol, RDC, étage, garage, espace aménagé) : **820 m²**',
          'Partie exonérée de la TNB = 5 × 820 = **4 100 m²**',
          'Partie frappée par la TNB = 8 000 − 4 100 = **3 900 m²**',
          'Si la parcelle ne faisait que 4 000 m² (inférieure à 4 100 m²), elle serait **entièrement exonérée** de la TNB.',
        ],
      },

      { type: 'h3', text: 'Base, annualité et obligations' },
      {
        type: 'p',
        text:
          "La **base imposable** est la **superficie du terrain au mètre carré** (toute fraction de m² étant comptée pour un m² entier). La taxe est **annuelle**, établie d’après les faits existants au **1ᵉʳ janvier**. Le propriétaire doit déposer une déclaration **avant le 1ᵉʳ mars** de chaque année et acquitter la taxe dans le même délai ; tout changement de propriétaire ou d’affectation doit être déclaré dans les **45 jours**.",
      },

      { type: 'h3', text: 'Des exonérations temporaires structurantes' },
      {
        type: 'p',
        text:
          'La loi prévoit d’importantes **exonérations temporaires**, particulièrement utiles aux promoteurs et lotisseurs :',
      },
      {
        type: 'list',
        items: [
          'terrains faisant l’objet d’un **permis de construire** : exonérés pendant **3 ans** ;',
          'terrains objet d’une **autorisation de lotir** : exonérés de **3 à 15 ans** selon la superficie (3 ans jusqu’à 20 ha, jusqu’à 15 ans au-delà de 400 ha) ;',
          'terrains affectés à une **exploitation professionnelle ou agricole** (dans la limite de 5 fois la superficie exploitée), ou difficiles à raccorder aux réseaux d’eau et d’électricité.',
        ],
      },
      {
        type: 'p',
        text:
          "**Attention** toutefois : si, à l’expiration des délais, les travaux ne sont pas achevés (ou moins de 50 % du lotissement réalisé), le redevable doit verser la taxe due au titre de la période d’exonération, **avec pénalités**.",
      },

      { type: 'h3', text: 'Focus : les terrains exonérés au titre de la loi n° 12-90 sur l’urbanisme' },
      {
        type: 'p',
        text:
          "Un cas d’exonération mérite une attention particulière. L’article 42 de la loi n° 47-06 exonère les terrains situés dans les **zones frappées d’interdiction de construire**, ainsi que ceux affectés à l’un des usages prévus aux paragraphes **2 à 8 de l’article 19 de la loi n° 12-90** relative à l’urbanisme. La logique est une logique d’équité : lorsqu’un plan d’aménagement réserve un terrain à un équipement public, à un espace vert, à la voirie ou à un site à protéger, son propriétaire ne peut pas le bâtir librement ; il serait donc injuste de le taxer comme un foncier constructible. La superficie ainsi grevée est **soustraite de la base de calcul** de la taxe.",
      },
      {
        type: 'p',
        text:
          'Les paragraphes 2 à 8 de l’article 19 de la loi n° 12-90 visent précisément les affectations suivantes :',
      },
      {
        type: 'callout',
        title: 'Les usages exonérés (article 19, §2 à 8, de la loi n° 12-90)',
        items: [
          '**§2 — Zones non aedificandi :** zones dans lesquelles toute construction est interdite.',
          '**§3 — Voirie :** voies, places et parkings à conserver, à modifier ou à créer.',
          '**§4 — Espaces verts et de loisirs :** espaces verts publics (boisements, parcs, jardins), terrains de jeux et espaces libres divers.',
          '**§5 — Espaces sportifs :** espaces destinés aux activités sportives à créer, à conserver ou à modifier.',
          '**§6 — Équipements publics :** équipements ferroviaires, sanitaires, culturels et d’enseignement, bâtiments administratifs, mosquées et cimetières.',
          '**§7 — Équipements collectifs d’intérêt général :** installations relevant du secteur privé, tels que centres commerciaux et centres de loisirs.',
          '**§8 — Patrimoine et sites à protéger :** quartiers, monuments, sites historiques ou archéologiques, sites et zones naturelles à protéger ou à mettre en valeur.',
        ],
      },
      {
        type: 'p',
        text:
          "En pratique, bénéficient aussi de cette exonération les terrains occupés par des **bidonvilles ou des constructions anarchiques** non encore libérés, les **terrains enclavés** entourés de toutes parts par des propriétés de tiers, ainsi que les terrains affectés aux équipements et services publics. Pour le propriétaire, identifier que sa parcelle relève de l’un de ces cas peut représenter une **économie de taxe substantielle**.",
      },

      { type: 'h2', text: '4. La réforme 2026 : un barème fondé sur le niveau d’équipement' },
      {
        type: 'p',
        text:
          "C’est le cœur de la **loi n° 14-25**. Jusqu’ici, le tarif de la TNB dépendait de la **catégorie urbanistique** du terrain (« zone immeuble » d’un côté, « zone villa / logement individuel » de l’autre). La réforme change radicalement de logique : le tarif est désormais fonction du **niveau réel d’équipement et de services publics** dont bénéficie le terrain. Un foncier bien desservi est davantage taxé ; un terrain mal équipé l’est beaucoup moins.",
      },
      { type: 'h3', text: 'Avant / après' },
      {
        type: 'table',
        headers: ['Ancien barème (jusqu’en 2025)', 'Nouveau barème (loi 14-25, dès 2026)'],
        rows: [
          ['Selon la catégorie urbanistique :', 'Selon le niveau d’équipement de la zone :'],
          ['Zone immeuble : 4 à 20 DH/m²', '**Zone bien équipée : 15 à 30 DH/m²**'],
          ['Zones villa, logement individuel et autres : 2 à 12 DH/m²', '**Zone moyennement équipée : 5 à 15 DH/m²**'],
          ['—', '**Zone faiblement équipée : 0,5 à 2 DH/m²**'],
        ],
      },
      { type: 'h3', text: 'Comment lire les trois nouvelles zones' },
      {
        type: 'list',
        items: [
          '**Zone bien équipée (15–30 DH/m²) :** présence de la plupart des équipements — voirie, eau potable, électricité, assainissement, éclairage public, transport urbain, collecte des déchets, établissements de santé et d’enseignement.',
          '**Zone moyennement équipée (5–15 DH/m²) :** dotée au moins de la voirie et des réseaux d’électricité et d’eau.',
          '**Zone faiblement équipée (0,5–2 DH/m²) :** dépourvue de tout ou de la plupart des équipements et réseaux de base.',
        ],
      },
      {
        type: 'p',
        text:
          "Concrètement, chaque **conseil communal** délimite ces zones et fixe le tarif applicable à l’intérieur des fourchettes légales, par décision de son président qui n’est exécutoire qu’après **visa du gouverneur** de la préfecture ou de la province. Le seuil de non-perception est maintenu : la taxe **inférieure à 200 DH** n’est ni émise ni payée. La réforme s’accompagne par ailleurs d’une réorganisation du recouvrement, confié aux **receveurs communaux** pour la TNB, tandis que la taxe d’habitation et la taxe de services communaux passent à la **Direction Générale des Impôts**.",
      },

      { type: 'h2', text: '5. Cas pratique : un terrain nu de 600 m²' },
      {
        type: 'p',
        text:
          "**M. C.** possède un terrain urbain non bâti de **600 m²**. Selon la zone d’équipement dans laquelle la commune le classe, et le tarif qu’elle retient dans la fourchette légale, la TNB annuelle varie fortement :",
      },
      {
        type: 'table',
        headers: ['Classement de la zone', 'Tarif retenu', 'TNB annuelle (600 m²)'],
        rows: [
          ['Zone bien équipée', '20 DH/m²', '**12 000 DH**'],
          ['Zone moyennement équipée', '10 DH/m²', '**6 000 DH**'],
          ['Zone faiblement équipée', '2 DH/m²', '**1 200 DH**'],
        ],
      },
      {
        type: 'p',
        text:
          "À surface identique, la facture peut donc varier de **1 200 à 12 000 DH par an** selon l’équipement de la zone. La réforme introduit ainsi une logique de **contribution proportionnée aux services rendus** : plus un terrain bénéficie d’infrastructures (et de valeur), plus il contribue ; un terrain enclavé et non viabilisé est, lui, faiblement taxé.",
      },

      { type: 'h2', text: '6. À retenir' },
      {
        type: 'callout',
        title: 'L’essentiel en 5 points',
        items: [
          '**Objet :** taxe locale annuelle sur les terrains urbains non bâtis, due par le propriétaire (ou le possesseur).',
          '**Base :** la superficie du terrain au m² ; déclaration et paiement avant le 1ᵉʳ mars de chaque année.',
          '**Règle des 5× :** sur une parcelle bâtie, l’excédent au-delà de 5 fois la superficie couverte est soumis à la TNB.',
          '**Réforme 2026 (loi 14-25) :** le tarif dépend désormais du niveau d’équipement — 15 à 30 DH/m² (bien équipée), 5 à 15 (moyenne), 0,5 à 2 (faible).',
          '**Exonérations :** permis de construire (3 ans), autorisation de lotir (3 à 15 ans) et terrains réservés par le plan d’aménagement (équipements, voirie, espaces verts…) au titre de la loi n° 12-90.',
        ],
      },

      { type: 'h2', text: '7. Foire aux questions (FAQ)' },
      {
        type: 'faq',
        items: [
          {
            q: 'Qui doit payer la TNB ?',
            a: 'Le **propriétaire** du terrain et, à défaut de propriétaire connu, le **possesseur**. En cas d’indivision, les co-indivisaires sont solidairement tenus du paiement.',
          },
          {
            q: 'Mon terrain est en dehors de toute ville : suis-je concerné ?',
            a: 'La TNB ne vise que les terrains situés dans les **périmètres urbains**, les centres délimités, les stations ou les zones couvertes par un plan d’aménagement. Un terrain purement rural et hors plan n’y est pas soumis.',
          },
          {
            q: 'Comment connaître le tarif qui s’applique à mon terrain ?',
            a: 'Depuis la loi 14-25, il dépend du **niveau d’équipement** de la zone (bien, moyennement ou faiblement équipée). Chaque commune délimite les zones et fixe le tarif dans la fourchette légale, par décision visée par le gouverneur.',
          },
          {
            q: 'Si je construis sur mon terrain, reste-t-il taxé à la TNB ?',
            a: 'Un terrain qui obtient un **permis de construire** est exonéré pendant **3 ans**, le temps d’édifier. Une fois la construction achevée, la parcelle relève en principe de la **taxe d’habitation** et non plus de la TNB.',
          },
          {
            q: 'Mon terrain est réservé à un équipement ou un espace vert par le plan d’aménagement : suis-je taxé ?',
            a: 'Non, tant que dure cette affectation. Les terrains frappés d’interdiction de construire ou réservés à l’un des usages prévus aux paragraphes 2 à 8 de l’article 19 de la loi n° 12-90 (voirie, espaces verts, équipements publics ou collectifs, sites à protéger…) sont **exonérés**, et leur superficie est soustraite du calcul de la taxe.',
          },
          {
            q: 'Y a-t-il un montant en dessous duquel rien n’est dû ?',
            a: 'Oui. Une TNB **inférieure à 200 DH** n’est ni émise ni payée.',
          },
          {
            q: 'Que se passe-t-il si je n’achève pas mon lotissement dans les délais ?',
            a: 'À l’expiration du délai d’exonération, si **moins de 50 %** des travaux de lotissement sont réalisés, la taxe due au titre de la période d’exonération devient exigible, **avec pénalités et majorations**.',
          },
        ],
      },

      { type: 'h2', text: 'Conclusion' },
      {
        type: 'p',
        text:
          "Avec la **loi n° 14-25**, la Taxe sur les Terrains Urbains Non Bâtis change de philosophie : elle relie désormais l’impôt à la **réalité de l’équipement et de la valeur du foncier**. Pour le propriétaire, cela signifie qu’un terrain bien situé et viabilisé coûtera davantage à conserver nu — une incitation supplémentaire à le valoriser — tandis qu’un terrain enclavé sera plus légèrement taxé.",
      },
      {
        type: 'p',
        text:
          "Anticiper la TNB, connaître le classement de sa parcelle et le calendrier des exonérations devient donc un élément clé de toute **stratégie foncière**. Chez **ATAA REALTY**, nous accompagnons propriétaires, investisseurs et promoteurs dans l’analyse fiscale et la valorisation de leur foncier, en lien avec les services communaux compétents.",
      },
      {
        type: 'disclaimer',
        text:
          "Cet article est fourni à titre d’information pédagogique et ne constitue pas un conseil fiscal personnalisé. Les tarifs effectifs dépendent des décisions de chaque conseil communal. Pour une situation précise, rapprochez-vous de votre commune ou d’un professionnel de la fiscalité.",
      },

      { type: 'h2', text: 'Références bibliographiques et réglementaires' },
      {
        type: 'references',
        items: [
          "**Loi n° 47-06** relative à la fiscalité des collectivités territoriales — articles 39 à 49 (taxe sur les terrains urbains non bâtis), telle que modifiée et complétée par la loi n° 07-20.",
          "**Loi n° 14-25** modifiant et complétant la loi n° 47-06, promulguée par le dahir n° 1-25-50 du 9 hija 1446 (6 juin 2025) — réécriture de l’article 45 (tarif de la TNB selon le niveau d’équipement).",
          "**Loi n° 12-90** relative à l’urbanisme (promulguée par le dahir n° 1-92-31 du 17 juin 1992) — article 19 (objet du plan d’aménagement), paragraphes 2 à 8 visés par l’exonération de TNB.",
          "**Médias24**, « Taxe sur les terrains non bâtis et fiscalité locale : ce que change la nouvelle loi n° 14-25 » (mai 2025).",
          "Support de formation « Fiscalité immobilière », **ONIGT–CRCS**, M. Salah Eddine EL KACIMI, MRICS, janvier 2026.",
        ],
      },
    ],
  },

  /* =====================================================================
   *  3. Droits d’enregistrement — Nouveauté 2026
   * ===================================================================== */
  {
    id: 3,
    date: '03 Février 2026',
    category: 'Fiscalité',
    title:
      "Les droits d’enregistrement dans l’immobilier au Maroc : barème, fonctionnement et la nouveauté 2026",
    subtitle:
      'Comprendre ce que l’on paie en achetant un bien — et le nouveau droit supplémentaire de 2 % sur les paiements en espèces',
    image:
      'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2000',
    excerpt:
      "La loi de finances 2026 instaure un droit d’enregistrement supplémentaire de 2 % sur les paiements en espèces. Barème, fonctionnement et conséquences concrètes pour acquéreurs et vendeurs.",
    content: [
      { type: 'h2', text: 'Introduction : pourquoi cet article ?' },
      {
        type: 'p',
        text:
          "Lorsqu’on achète un appartement, une maison ou un terrain au Maroc, le prix affiché n’est jamais le coût final. À la signature s’ajoutent des **frais d’acquisition**, dont la composante la plus connue est constituée par les **droits d’enregistrement**. Souvent méconnus de l’acheteur, ils représentent pourtant une part significative du budget d’une transaction immobilière.",
      },
      {
        type: 'p',
        text:
          "La **loi de finances 2026** a introduit une mesure qui change la donne pour de nombreuses opérations : un **droit d’enregistrement supplémentaire de 2 %** visant les paiements en espèces ou non traçables. Cet article, conçu de façon pédagogique, explique d’abord ce que sont les droits d’enregistrement et comment ils se calculent, avant de détailler cette nouveauté et ses conséquences concrètes pour les acquéreurs et les vendeurs.",
      },

      { type: 'h2', text: '1. Définition : que sont les droits d’enregistrement ?' },
      {
        type: 'p',
        text:
          "Les **droits d’enregistrement** sont une somme perçue par l’administration en contrepartie de la formalité de l’**enregistrement d’un acte** — c’est-à-dire son inscription officielle, qui lui confère **date certaine et opposabilité**. En matière immobilière, ils sont dus à l’occasion des **mutations** : ventes, échanges, donations, mais aussi certains baux de longue durée ou cessions de parts de sociétés immobilières.",
      },
      {
        type: 'p',
        text:
          "Contrairement à la **TPI** — qui frappe le profit du vendeur — les droits d’enregistrement frappent la **transaction elle-même** et sont, en pratique, supportés par l’**acquéreur**. La formalité est généralement accomplie par le notaire ou l’adoul rédacteur de l’acte, qui collecte les droits et les reverse au Trésor.",
      },

      { type: 'h2', text: '2. Le cadre juridique marocain' },
      {
        type: 'p',
        text:
          "Les droits d’enregistrement sont régis par le **Code Général des Impôts (CGI)**, actualisé chaque année par la loi de finances. Les articles structurants en matière immobilière sont les suivants :",
      },
      {
        type: 'table',
        headers: ['Article du CGI', 'Objet'],
        rows: [
          ['126 – 127', 'Champ d’application : actes et mutations soumis à l’enregistrement.'],
          ['128', 'Délais : enregistrement et paiement dans les 30 jours.'],
          ['129', 'Exonérations.'],
          ['131', 'Base imposable : prix exprimé, charges et valeur vénale.'],
          ['133', '**Barème** des droits proportionnels (1 % à 6 %) et **droit supplémentaire de 2 %** (nouveauté 2026).'],
          ['135', 'Droits fixes (200 DH et 1 000 DH).'],
        ],
      },

      { type: 'h2', text: '3. Comment se calculent les droits d’enregistrement ?' },
      { type: 'h3', text: 'La base imposable' },
      {
        type: 'p',
        text:
          "Pour les ventes et autres transmissions à titre onéreux, la **base** est constituée par le **prix exprimé dans l’acte**, augmenté des charges qui s’y ajoutent (article 131). L’administration conserve toutefois un droit de contrôle de la valeur : si le prix déclaré est manifestement inférieur à la **valeur vénale** du bien, elle peut le redresser. Pour les donations, la base est la **valeur déclarée des biens**, sans déduction des charges.",
      },

      { type: 'h3', text: 'Le barème des taux applicables à l’immobilier' },
      {
        type: 'table',
        headers: ['Taux', 'Opérations concernées'],
        rows: [
          ['**4 %**', 'Acquisition de **locaux construits** (habitation, commercial, professionnel ou administratif) et des terrains attenants, dans la limite de 5 fois la superficie couverte.'],
          ['**5 %**', 'Acquisition de **terrains nus** ou de terrains avec constructions destinées à être démolies ; cessions d’actions ou parts de sociétés immobilières transparentes et à prépondérance immobilière non cotées.'],
          ['**6 %**', 'Baux de **plus de 10 ans**, rentes perpétuelles, cessions de droit au bail ; acquisitions d’immeubles par les établissements de crédit et les entreprises d’assurances.'],
          ['**3 %**', 'Première vente de **logements sociaux** et de logements à faible valeur immobilière.'],
          ['**1,5 %**', '**Donations** (cessions à titre gratuit) en ligne directe, entre époux, entre frères et sœurs ; partages ; constitutions d’hypothèque.'],
        ],
      },
      {
        type: 'p',
        text:
          "À ces droits proportionnels s’ajoutent un **minimum de perception de 100 DH** et, pour certains actes, des **droits fixes de 200 DH ou 1 000 DH** (article 135). L’ensemble doit être réglé, avec l’accomplissement de la formalité, dans un **délai de 30 jours** à compter de la date de l’acte (article 128).",
      },

      { type: 'h2', text: '4. La nouveauté 2026 : le droit supplémentaire de 2 %' },
      {
        type: 'p',
        text:
          "C’est la mesure phare de la **loi de finances 2026 (loi n° 50-25)** en matière de droits d’enregistrement. Le nouvel **article 133-III du CGI** institue un **droit d’enregistrement supplémentaire de 2 %**, qui s’ajoute au droit proportionnel habituel, pour lutter contre les paiements en liquide et renforcer la traçabilité des transactions.",
      },
      {
        type: 'callout',
        title: 'Ce que dit la loi (article 133-III, en vigueur le 1ᵉʳ juillet 2026)',
        items: [
          '**Opérations visées :** les mutations à titre onéreux de biens immeubles, de droits réels immobiliers ou de fonds de commerce.',
          '**Seuil d’éligibilité — il porte sur le PRIX de la transaction :** la mesure ne s’applique qu’aux biens dont le prix de cession dépasse **300 000 DH**. Si ce prix est inférieur ou égal à 300 000 DH, **aucun droit supplémentaire** n’est dû, quel que soit le mode de paiement.',
          '**Conditions de déclenchement :** l’acte ne mentionne pas les modalités et références de règlement, **OU** le prix n’est pas réglé par un **moyen de paiement traçable**.',
          '**Assiette du droit de 2 % — elle porte sur le MONTANT non traçable :** les 2 % se calculent sur la **somme réglée en espèces**. Si la totalité du prix est non traçable, ils portent sur l’intégralité ; si seule une partie est payée en espèces, ils ne portent que sur cette partie.',
          '**Entrée en vigueur :** le **1ᵉʳ juillet 2026**.',
        ],
      },
      {
        type: 'p',
        text:
          "Il faut donc bien distinguer **deux montants différents**. D’un côté, le **prix de la transaction**, qui sert uniquement à savoir si la mesure s’applique (seuil de 300 000 DH apprécié sur le prix total du bien). De l’autre, le **montant réglé en espèces**, qui sert de base de calcul des 2 %. Le seuil de 300 000 DH n’est donc pas un seuil sur la part payée en liquide : un bien vendu 2 000 000 DH dont seulement 50 000 DH sont réglés en espèces est éligible (prix > 300 000 DH), mais la majoration de 2 % ne frappera que ces 50 000 DH.",
      },
      {
        type: 'p',
        text:
          "Les **moyens de paiement traçables** qui permettent d’échapper au droit supplémentaire sont, par renvoi à l’article 11-II du CGI : le **chèque barré non endossable**, l’**effet de commerce**, le **moyen magnétique de paiement**, le **virement bancaire**, le **procédé électronique** ou la **compensation**. En pratique, un acquéreur qui règle son acquisition par virement et le mentionne clairement dans l’acte n’est pas concerné par la majoration.",
      },
      {
        type: 'p',
        text:
          "L’objectif de la mesure est double : **décourager les règlements en liquide** — souvent associés à la sous-déclaration des prix — et **améliorer la traçabilité des flux** dans un secteur où les paiements en espèces restaient fréquents.",
      },

      { type: 'h2', text: '5. Cas pratique : l’achat d’un appartement à 1 500 000 DH' },
      {
        type: 'p',
        text:
          "**M. B.** achète un appartement (local construit, taux de 4 %) au prix de **1 500 000 DH**. Le droit proportionnel de base s’élève donc à 4 % × 1 500 000 = **60 000 DH**. Selon le mode de paiement retenu, voici l’impact de la nouveauté 2026 :",
      },
      {
        type: 'table',
        headers: ['Modalité de paiement', 'Droit supplémentaire 2 %', 'Total des droits'],
        rows: [
          ['Totalité par **virement bancaire** (mentionné dans l’acte)', '**0 DH**', '**60 000 DH**'],
          ['400 000 DH payés en espèces, le reste par virement', '2 % × 400 000 = **8 000 DH**', '**68 000 DH**'],
          ['Acte ne mentionnant **aucune modalité** de paiement', '2 % × 1 500 000 = **30 000 DH**', '**90 000 DH**'],
        ],
      },
      {
        type: 'p',
        text:
          "La lecture est claire : à bien et prix identiques, le coût des droits varie du simple (60 000 DH) à **1,5 fois plus** (90 000 DH) selon la rigueur apportée au mode de règlement et à sa mention dans l’acte. **Régler par un moyen traçable et le documenter dans l’acte** est donc la voie la plus économique et la plus sûre. À l’inverse, si M. B. avait acheté un parking à 250 000 DH réglé intégralement en espèces, aucun droit supplémentaire ne serait dû : le prix de la transaction n’atteint pas le seuil de 300 000 DH.",
      },

      { type: 'h2', text: '6. À retenir' },
      {
        type: 'callout',
        title: 'L’essentiel en 5 points',
        items: [
          '**Nature :** les droits d’enregistrement frappent la transaction (la mutation) et sont, en pratique, à la charge de l’acquéreur.',
          '**Barème immobilier :** 4 % pour les locaux construits, 5 % pour les terrains nus, 6 % pour les baux longue durée, 3 % pour le logement social, 1,5 % pour les donations familiales.',
          '**Délai :** enregistrement et paiement dans les 30 jours, via le notaire ou l’adoul.',
          '**Nouveauté 2026 (1ᵉʳ juillet 2026) :** droit supplémentaire de 2 % lorsque le prix du bien dépasse 300 000 DH et qu’une partie est réglée en espèces ou de façon non traçable. Le seuil de 300 000 DH s’apprécie sur le prix total ; les 2 % se calculent sur le seul montant payé en espèces.',
          '**Bon réflexe :** payer par virement (ou autre moyen traçable) et mentionner les références de règlement dans l’acte.',
        ],
      },

      { type: 'h2', text: '7. Foire aux questions (FAQ)' },
      {
        type: 'faq',
        items: [
          {
            q: 'Qui paie les droits d’enregistrement, l’acheteur ou le vendeur ?',
            a: 'En pratique, ils sont supportés par l’**acquéreur**. La formalité et le paiement sont accomplis par le notaire ou l’adoul qui rédige l’acte.',
          },
          {
            q: 'Quel taux s’applique à l’achat d’un appartement ?',
            a: 'Le taux de **4 %**, applicable aux locaux construits à usage d’habitation, commercial, professionnel ou administratif, ainsi qu’aux terrains attenants dans la limite de 5 fois la superficie couverte.',
          },
          {
            q: 'Dans quel délai faut-il enregistrer l’acte ?',
            a: 'Dans les **30 jours** suivant la date de l’acte (article 128 du CGI).',
          },
          {
            q: 'Le seuil de 300 000 DH concerne-t-il le prix du bien ou le montant payé en espèces ?',
            a: 'Il concerne le **prix de la transaction**. La mesure ne s’applique que si le prix du bien dépasse 300 000 DH ; en deçà, aucun supplément n’est dû, même en cas de paiement en espèces. Une fois ce seuil franchi, les 2 % se calculent, eux, sur le **montant réglé en espèces** (ou non traçable).',
          },
          {
            q: 'Le droit supplémentaire de 2 % s’applique-t-il à tous les achats ?',
            a: 'Non. Il vise les **mutations à titre onéreux** dont le prix dépasse 300 000 DH et qui sont réglées en espèces ou par un moyen non traçable, ou dont l’acte ne mentionne pas les modalités de paiement. Il s’applique **à compter du 1ᵉʳ juillet 2026**.',
          },
          {
            q: 'Et si je paie une partie en espèces et le reste par virement ?',
            a: 'Le droit supplémentaire de 2 % ne s’applique qu’à la **fraction du prix payée en espèces**, et non à la totalité. Par exemple, sur un bien à 800 000 DH dont 100 000 DH en espèces, la majoration est de 2 % × 100 000 = **2 000 DH**.',
          },
          {
            q: 'Les donations entre parents et enfants sont-elles touchées par le droit de 2 % ?',
            a: 'Non : la majoration vise les **mutations à titre onéreux**. Les donations en ligne directe, entre époux ou entre frères et sœurs relèvent du taux réduit de **1,5 %**.',
          },
        ],
      },

      { type: 'h2', text: 'Conclusion' },
      {
        type: 'p',
        text:
          "Les **droits d’enregistrement** constituent un poste de coût incontournable de toute acquisition immobilière, qu’il convient d’anticiper dès la phase de négociation. Avec la **loi de finances 2026**, leur logique se double d’un **objectif de transparence** : le droit supplémentaire de 2 % récompense, de fait, les transactions réglées par des moyens traçables et clairement documentées.",
      },
      {
        type: 'p',
        text:
          "Pour l’acheteur comme pour le vendeur, la meilleure protection consiste à **structurer le paiement par voie bancaire** et à veiller à la **qualité rédactionnelle de l’acte**. Chez **ATAA REALTY**, nous accompagnons nos clients dans l’estimation des biens, le chiffrage complet des frais d’acquisition et la sécurisation de leurs opérations, en lien avec les professionnels du droit.",
      },
      {
        type: 'disclaimer',
        text:
          "Cet article est fourni à titre d’information pédagogique et ne constitue pas un conseil fiscal personnalisé. Pour une opération précise, rapprochez-vous d’un notaire ou d’un professionnel de la fiscalité.",
      },

      { type: 'h2', text: 'Références bibliographiques et réglementaires' },
      {
        type: 'references',
        items: [
          "**Code Général des Impôts (CGI), édition 2026** — articles 126 à 135 (champ d’application, délais, exonérations, base imposable, barème des droits et droit fixe), et article 11-II (moyens de paiement).",
          "**Loi de finances n° 50-25 pour l’année budgétaire 2026** — article 7 instituant le droit d’enregistrement supplémentaire de 2 % (article 133-III du CGI), applicable à compter du 1ᵉʳ juillet 2026.",
          "**Médias24**, « Mutations immobilières et fonds de commerce : un droit d’enregistrement supplémentaire de 2 % à partir de juillet 2026 » (janvier 2026).",
          "Support de formation « Fiscalité immobilière », **ONIGT–CRCS**, M. Salah Eddine EL KACIMI, MRICS, janvier 2026.",
        ],
      },
    ],
  },
];
